import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { useMutation, useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { MagnifyingGlassIcon, PlusIcon } from '@heroicons/react/24/outline';
import api from '../../services/api';
import { Modal } from '../../components/ui/Modal';
import { Pagination } from '../../components/ui/Pagination';
import {
  badgeClass,
  dangerButtonClass,
  DetailCard,
  DetailItem,
  Field,
  FormActions,
  FormCard,
  formatNumber,
  getApiErrorMessage,
  inputClass,
  inputErrorClass,
  primaryButtonClass,
  secondaryButtonClass,
} from '../../components/ui/FormControls';
import { usePermissions } from '../../hooks/usePermissions';
import { cn } from '../../lib/utils';
import { defaultRowsPerPage } from '../../lib/preferences';

type Status = 'open' | 'in_progress' | 'closed' | 'cancelled';
type Severity = 'minor' | 'major' | 'critical';
type Source = 'incoming' | 'in_process' | 'final' | 'customer_complaint' | 'other';
type Disposition = 'pending' | 'use_as_is' | 'rework' | 'scrap' | 'return_to_vendor';

interface Ncr {
  id: string;
  ncrNumber: string;
  source: Source;
  severity: Severity;
  status: Status;
  productName: string | null;
  sku?: string | null;
  unit?: string | null;
  vendorName: string | null;
  inspectionLotNumber?: string | null;
  reference: string | null;
  quantity: number | null;
  description: string;
  disposition: Disposition;
  dispositionNotes?: string | null;
  rootCause?: string | null;
  correctiveAction?: string | null;
  preventiveAction?: string | null;
  responsibleName: string | null;
  targetDate: string | null;
  verification?: string | null;
  overdue: boolean;
  createdAt: string;
  closedAt: string | null;
}
interface NcrPage {
  data: Ncr[];
  total: number;
  totalPages: number;
}
interface NcrSummary {
  open: number;
  critical: number;
  overdue: number;
  awaitingDisposition: number;
  closedThisMonth: number;
  averageDaysToClose: number;
}

const PAGE_SIZES = [10, 25, 50, 100];
const thClass = 'px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500';
const STATUS: Record<Status, { label: string; className: string }> = {
  open: { label: 'Open', className: 'bg-amber-50 text-amber-800 ring-amber-600/20' },
  in_progress: { label: 'In progress', className: 'bg-blue-50 text-blue-700 ring-blue-600/20' },
  closed: { label: 'Closed', className: 'bg-green-50 text-green-700 ring-green-600/20' },
  cancelled: { label: 'Cancelled', className: 'bg-gray-50 text-gray-600 ring-gray-500/20' },
};
const SEVERITY: Record<Severity, { label: string; className: string }> = {
  minor: { label: 'Minor', className: 'bg-gray-50 text-gray-700 ring-gray-500/20' },
  major: { label: 'Major', className: 'bg-orange-50 text-orange-800 ring-orange-600/20' },
  critical: { label: 'Critical', className: 'bg-red-50 text-red-700 ring-red-600/20' },
};
const SOURCES: Record<Source, string> = { incoming: 'Incoming goods', in_process: 'In process', final: 'Final inspection', customer_complaint: 'Customer complaint', other: 'Other' };
const DISPOSITIONS: Record<Disposition, string> = { pending: 'Not decided', use_as_is: 'Use as is', rework: 'Rework', scrap: 'Scrap', return_to_vendor: 'Return to vendor' };

const formatDate = (value?: string | null) =>
  value ? new Date(String(value).length <= 10 ? `${value}T00:00:00` : value).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
const clean = (params: object) => Object.fromEntries(Object.entries(params).filter(([, value]) => value !== undefined && value !== '' && value !== false));

const ncrApi = {
  page: async (params: object): Promise<NcrPage> => (await api.get('/ncrs', { params: clean(params) })).data,
  summary: async (): Promise<NcrSummary> => (await api.get('/ncrs/summary')).data,
  one: async (id: string): Promise<Ncr> => (await api.get(`/ncrs/${id}`)).data,
  create: async (data: object): Promise<Ncr> => (await api.post('/ncrs', data)).data,
  update: async (id: string, data: object): Promise<Ncr> => (await api.patch(`/ncrs/${id}`, data)).data,
  close: async (id: string, verification: string): Promise<Ncr> => (await api.post(`/ncrs/${id}/close`, { verification })).data,
  cancel: async (id: string, reason: string): Promise<Ncr> => (await api.post(`/ncrs/${id}/cancel`, { reason })).data,
};

// ---------------------------------------------------------------- raise a report

const RaiseForm: React.FC<{ lotId: string; isPending: boolean; onCancel: () => void; onSubmit: (data: object) => void }> = ({ lotId, isPending, onCancel, onSubmit }) => {
  const { data: lot } = useQuery({ queryKey: ['quality', 'lot', lotId], queryFn: async () => (await api.get(`/inspections/${lotId}`)).data, enabled: !!lotId });
  const [form, setForm] = useState({ source: (lotId ? 'incoming' : 'in_process') as Source, severity: 'minor' as Severity, reference: '', quantity: '', description: '', responsibleName: '', targetDate: '' });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const set = (field: keyof typeof form) => (event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => setForm((prev) => ({ ...prev, [field]: event.target.value }));

  // A report on a lot starts with what the inspection said
  useEffect(() => {
    if (lot?.rejectionReason) setForm((prev) => (prev.description ? prev : { ...prev, description: lot.rejectionReason }));
  }, [lot]);

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    const next: Record<string, string> = {};
    if (form.description.trim().length < 5) next.description = 'Describe what is wrong';
    const quantity = form.quantity.trim() === '' ? null : Number(form.quantity);
    if (quantity !== null && (!Number.isFinite(quantity) || quantity < 0)) next.quantity = 'Enter a quantity';
    setErrors(next);
    if (Object.keys(next).length > 0) return;
    onSubmit({
      ...(lotId ? { inspectionLotId: lotId } : { source: form.source, reference: form.reference.trim() || null }),
      severity: form.severity,
      quantity: quantity ?? undefined,
      description: form.description.trim(),
      responsibleName: form.responsibleName.trim() || null,
      targetDate: form.targetDate || null,
    });
  };

  return (
    <form onSubmit={handleSubmit} noValidate className="space-y-5">
      {lotId && lot && (
        <div className="rounded-lg bg-gray-50 p-4 text-sm ring-1 ring-gray-200">
          <p className="font-semibold text-gray-900">{lot.productName} · {lot.lotNumber}</p>
          <p className="text-xs text-gray-500">
            {[lot.grnNumber, lot.vendorName, `${formatNumber(Number(lot.rejectedQuantity))} of ${formatNumber(Number(lot.quantity))} rejected`].filter(Boolean).join(' · ')}
          </p>
        </div>
      )}
      <FormCard title="What is wrong" description="The non-conformance as it was found">
        {!lotId && (
          <Field label="Found at" htmlFor="ncr-source">
            <select id="ncr-source" value={form.source} onChange={set('source')} className={inputClass}>
              {(Object.keys(SOURCES) as Source[]).map((source) => (<option key={source} value={source}>{SOURCES[source]}</option>))}
            </select>
          </Field>
        )}
        <Field label="Severity" htmlFor="ncr-severity">
          <select id="ncr-severity" value={form.severity} onChange={set('severity')} className={inputClass}>
            {(Object.keys(SEVERITY) as Severity[]).map((severity) => (<option key={severity} value={severity}>{SEVERITY[severity].label}</option>))}
          </select>
        </Field>
        {!lotId && (
          <Field label="Reference" htmlFor="ncr-reference" hint="Order, work order, challan or complaint number">
            <input id="ncr-reference" type="text" maxLength={60} value={form.reference} onChange={set('reference')} className={inputClass} />
          </Field>
        )}
        <Field label="Quantity affected" htmlFor="ncr-quantity" error={errors.quantity} hint={lotId ? 'Left empty, the rejected quantity of the lot' : undefined}>
          <input id="ncr-quantity" type="number" min="0" step="0.0001" value={form.quantity} onChange={set('quantity')} className={cn(inputClass, 'text-right tabular-nums', errors.quantity && inputErrorClass)} />
        </Field>
        <Field label="Description" htmlFor="ncr-description" required error={errors.description} className="sm:col-span-2">
          <textarea id="ncr-description" rows={3} maxLength={2000} value={form.description} onChange={set('description')} className={cn(inputClass, errors.description && inputErrorClass)} placeholder="What is wrong, where, and how it was found" />
        </Field>
        <Field label="Responsible" htmlFor="ncr-responsible">
          <input id="ncr-responsible" type="text" maxLength={100} value={form.responsibleName} onChange={set('responsibleName')} className={inputClass} />
        </Field>
        <Field label="Target date" htmlFor="ncr-target">
          <input id="ncr-target" type="date" value={form.targetDate} onChange={set('targetDate')} className={inputClass} />
        </Field>
      </FormCard>
      <FormActions>
        <button type="button" onClick={onCancel} disabled={isPending} className={secondaryButtonClass}>Cancel</button>
        <button type="submit" disabled={isPending} className={primaryButtonClass}>{isPending ? 'Saving…' : 'Raise Report'}</button>
      </FormActions>
    </form>
  );
};

// ---------------------------------------------------------------- one report: disposition, cause, actions, closing

const NcrPanel: React.FC<{ ncrId: string; canChange: boolean; onClose: () => void }> = ({ ncrId, canChange, onClose }) => {
  const queryClient = useQueryClient();
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['ncrs'] });
  const { data: ncr, isLoading, isError, error } = useQuery({ queryKey: ['ncrs', 'one', ncrId], queryFn: () => ncrApi.one(ncrId) });
  const save = useMutation({ mutationFn: (data: object) => ncrApi.update(ncrId, data), onSuccess: invalidate });
  const close = useMutation({ mutationFn: (verification: string) => ncrApi.close(ncrId, verification), onSuccess: invalidate });
  const cancel = useMutation({ mutationFn: (reason: string) => ncrApi.cancel(ncrId, reason), onSuccess: invalidate });

  const [form, setForm] = useState<{ severity: Severity; disposition: Disposition; dispositionNotes: string; rootCause: string; correctiveAction: string; preventiveAction: string; responsibleName: string; targetDate: string } | null>(null);
  const [mode, setMode] = useState<'edit' | 'close' | 'cancel'>('edit');
  const [text, setText] = useState('');

  // The stored report fills the form once
  useEffect(() => {
    if (ncr && form === null) {
      setForm({ severity: ncr.severity, disposition: ncr.disposition, dispositionNotes: ncr.dispositionNotes ?? '', rootCause: ncr.rootCause ?? '', correctiveAction: ncr.correctiveAction ?? '', preventiveAction: ncr.preventiveAction ?? '', responsibleName: ncr.responsibleName ?? '', targetDate: ncr.targetDate ?? '' });
    }
  }, [ncr, form]);

  if (isLoading || !form) return <p className="py-10 text-center text-sm text-gray-500">{isError ? getApiErrorMessage(error, 'The report could not be loaded.') : 'Loading…'}</p>;
  if (!ncr) return null;
  const editable = canChange && (ncr.status === 'open' || ncr.status === 'in_progress');
  const set = (field: keyof NonNullable<typeof form>) => (event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => setForm((prev) => (prev ? { ...prev, [field]: event.target.value } : prev));
  const busy = save.isPending || close.isPending || cancel.isPending;
  const missing = [form.disposition === 'pending' ? 'the disposition' : null, !form.rootCause.trim() ? 'the root cause' : null, !form.correctiveAction.trim() ? 'the corrective action' : null].filter(Boolean);
  const onError = (fallback: string) => (actionError: unknown) => toast.error(getApiErrorMessage(actionError, fallback));

  const handleSave = (then?: () => void) =>
    save.mutate(
      { ...form, dispositionNotes: form.dispositionNotes.trim(), rootCause: form.rootCause.trim(), correctiveAction: form.correctiveAction.trim(), preventiveAction: form.preventiveAction.trim(), responsibleName: form.responsibleName.trim(), targetDate: form.targetDate },
      { onSuccess: () => (then ? then() : toast.success(`${ncr.ncrNumber} saved`)), onError: onError('The report could not be saved') },
    );

  return (
    <div className="space-y-5">
      <DetailCard title="Non-conformance" columns={4}>
        <DetailItem label="Found at">{SOURCES[ncr.source]}</DetailItem>
        <DetailItem label="Status"><span className={cn(badgeClass, STATUS[ncr.status].className)}>{STATUS[ncr.status].label}</span></DetailItem>
        <DetailItem label="Raised on">{formatDate(ncr.createdAt)}</DetailItem>
        <DetailItem label="Quantity">{ncr.quantity === null ? '—' : `${formatNumber(ncr.quantity)} ${ncr.unit ?? ''}`}</DetailItem>
        <DetailItem label="Product">{ncr.productName || '—'}</DetailItem>
        <DetailItem label="Vendor">{ncr.vendorName || '—'}</DetailItem>
        <DetailItem label="Reference" className="col-span-2">{ncr.reference || '—'}</DetailItem>
        <DetailItem label="Description" className="col-span-2 lg:col-span-4"><span className="whitespace-pre-line font-normal">{ncr.description}</span></DetailItem>
        {ncr.verification && <DetailItem label={ncr.status === 'cancelled' ? 'Outcome' : 'Verified by'} className="col-span-2 lg:col-span-4"><span className="whitespace-pre-line font-normal">{ncr.verification}</span></DetailItem>}
      </DetailCard>

      <FormCard title="Disposition" description="What is done with the non-conforming material. A record of the decision; stock that is scrapped is written off on a scrap note.">
        <Field label="Severity" htmlFor="np-severity">
          <select id="np-severity" disabled={!editable} value={form.severity} onChange={set('severity')} className={inputClass}>
            {(Object.keys(SEVERITY) as Severity[]).map((severity) => (<option key={severity} value={severity}>{SEVERITY[severity].label}</option>))}
          </select>
        </Field>
        <Field label="Disposition" htmlFor="np-disposition">
          <select id="np-disposition" disabled={!editable} value={form.disposition} onChange={set('disposition')} className={inputClass}>
            {(Object.keys(DISPOSITIONS) as Disposition[]).map((disposition) => (<option key={disposition} value={disposition}>{DISPOSITIONS[disposition]}</option>))}
          </select>
        </Field>
        <Field label="Disposition notes" htmlFor="np-notes" className="sm:col-span-2">
          <input id="np-notes" type="text" maxLength={2000} disabled={!editable} value={form.dispositionNotes} onChange={set('dispositionNotes')} className={inputClass} />
        </Field>
        {ncr.disposition === 'scrap' && ncr.status !== 'cancelled' && (
          <p className="text-sm text-gray-600 sm:col-span-2">
            If the material is in stock,{' '}
            <Link to={`/scrap?ncr=${ncr.id}`} className="font-semibold text-indigo-600 hover:text-indigo-500">
              write it off on a scrap note
            </Link>
            .
          </p>
        )}
      </FormCard>

      <FormCard title="Corrective and preventive action" description="Why it happened, what was done about it, and what stops it happening again">
        <Field label="Root cause" htmlFor="np-cause" className="sm:col-span-2">
          <textarea id="np-cause" rows={2} maxLength={2000} disabled={!editable} value={form.rootCause} onChange={set('rootCause')} className={inputClass} />
        </Field>
        <Field label="Corrective action" htmlFor="np-corrective" className="sm:col-span-2">
          <textarea id="np-corrective" rows={2} maxLength={2000} disabled={!editable} value={form.correctiveAction} onChange={set('correctiveAction')} className={inputClass} />
        </Field>
        <Field label="Preventive action" htmlFor="np-preventive" className="sm:col-span-2">
          <textarea id="np-preventive" rows={2} maxLength={2000} disabled={!editable} value={form.preventiveAction} onChange={set('preventiveAction')} className={inputClass} />
        </Field>
        <Field label="Responsible" htmlFor="np-responsible">
          <input id="np-responsible" type="text" maxLength={100} disabled={!editable} value={form.responsibleName} onChange={set('responsibleName')} className={inputClass} />
        </Field>
        <Field label="Target date" htmlFor="np-target" error={ncr.overdue ? 'Past the target date' : undefined}>
          <input id="np-target" type="date" disabled={!editable} value={form.targetDate} onChange={set('targetDate')} className={inputClass} />
        </Field>
      </FormCard>

      {mode === 'close' && (
        <div className="space-y-3 rounded-lg bg-green-50 p-4 ring-1 ring-green-600/20">
          <Field label="How was it checked that the action worked?" htmlFor="np-verification" required>
            <textarea id="np-verification" rows={2} maxLength={2000} autoFocus value={text} onChange={(event) => setText(event.target.value)} className={inputClass} placeholder="For example: the next three batches were free of the defect" />
          </Field>
        </div>
      )}
      {mode === 'cancel' && (
        <div className="space-y-3 rounded-lg bg-red-50 p-4 ring-1 ring-red-600/20">
          <Field label="Why is the report cancelled?" htmlFor="np-cancel" required>
            <input id="np-cancel" type="text" maxLength={300} autoFocus value={text} onChange={(event) => setText(event.target.value)} className={inputClass} />
          </Field>
        </div>
      )}

      <FormActions>
        {mode === 'edit' ? (
          <>
            <button type="button" onClick={onClose} disabled={busy} className={secondaryButtonClass}>Close Window</button>
            {editable && <button type="button" onClick={() => { setText(''); setMode('cancel'); }} disabled={busy} className={secondaryButtonClass}>Cancel Report</button>}
            {editable && <button type="button" onClick={() => handleSave()} disabled={busy} className={secondaryButtonClass}>{save.isPending ? 'Saving…' : 'Save'}</button>}
            {editable && (
              <button type="button" onClick={() => { setText(''); setMode('close'); }} disabled={busy || missing.length > 0} title={missing.length > 0 ? `Still to enter: ${missing.join(', ')}` : undefined} className={primaryButtonClass}>
                Close Report
              </button>
            )}
          </>
        ) : (
          <>
            <button type="button" onClick={() => setMode('edit')} disabled={busy} className={secondaryButtonClass}>Back</button>
            {mode === 'close' ? (
              <button
                type="button"
                disabled={busy || text.trim().length < 5}
                // what was typed above is saved first, so the report closes with it
                onClick={() => handleSave(() => close.mutate(text.trim(), { onSuccess: () => { toast.success(`${ncr.ncrNumber} closed`); setMode('edit'); }, onError: onError('The report could not be closed') }))}
                className={primaryButtonClass}
              >
                {busy ? 'Closing…' : 'Confirm Closing'}
              </button>
            ) : (
              <button type="button" disabled={busy || text.trim().length < 3} onClick={() => cancel.mutate(text.trim(), { onSuccess: () => { toast.success(`${ncr.ncrNumber} cancelled`); setMode('edit'); }, onError: onError('The report could not be cancelled') })} className={dangerButtonClass}>
                {cancel.isPending ? 'Cancelling…' : 'Cancel Report'}
              </button>
            )}
          </>
        )}
      </FormActions>
      {editable && mode === 'edit' && missing.length > 0 && <p className="text-right text-xs text-gray-500">To close the report, enter {missing.join(', ')}.</p>}
    </div>
  );
};

// ---------------------------------------------------------------- page

const NonConformances: React.FC = () => {
  const { can } = usePermissions();
  const canCreate = can('quality.create');
  const canChange = can('quality.update');
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();

  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<Status | ''>('');
  const [severityFilter, setSeverityFilter] = useState<Severity | ''>('');
  const [overdueOnly, setOverdueOnly] = useState(false);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(defaultRowsPerPage);
  // "Raise NCR" on an inspection lot opens the form for that lot
  const lotFromLink = searchParams.get('lot') ?? '';
  const [formOpen, setFormOpen] = useState(!!lotFromLink);
  const [viewingId, setViewingId] = useState<string | null>(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const params = { page, limit, search: search || undefined, status: statusFilter, severity: severityFilter, overdueOnly };
  const { data, isLoading, isError, error, isFetching } = useQuery({ queryKey: ['ncrs', 'page', params], queryFn: () => ncrApi.page(params), placeholderData: keepPreviousData });
  const { data: summary } = useQuery({ queryKey: ['ncrs', 'summary'], queryFn: ncrApi.summary });
  const create = useMutation({ mutationFn: ncrApi.create, onSuccess: () => queryClient.invalidateQueries({ queryKey: ['ncrs'] }) });

  const rows = data?.data ?? [];
  const total = data?.total ?? 0;
  const totalPages = data?.totalPages ?? 1;
  const viewing = rows.find((row) => row.id === viewingId);
  useEffect(() => {
    if (data && page > data.totalPages) setPage(data.totalPages);
  }, [data, page]);

  const closeForm = () => {
    if (create.isPending) return;
    setFormOpen(false);
    if (lotFromLink) setSearchParams({}, { replace: true });
  };
  const hasFilters = !!searchInput || !!statusFilter || !!severityFilter || overdueOnly;
  const stats = [
    { label: 'Open reports', value: summary ? formatNumber(summary.open) : '—', hint: `${summary ? formatNumber(summary.critical) : '—'} critical` },
    { label: 'Past target date', value: summary ? formatNumber(summary.overdue) : '—', hint: 'Open and overdue' },
    { label: 'Awaiting disposition', value: summary ? formatNumber(summary.awaitingDisposition) : '—', hint: 'Material not yet decided on' },
    { label: 'Closed this month', value: summary ? formatNumber(summary.closedThisMonth) : '—', hint: summary && summary.averageDaysToClose > 0 ? `${formatNumber(summary.averageDaysToClose, 1)} day(s) to close on average` : 'No report closed yet' },
  ];
  const firstRow = total > 0 ? (page - 1) * limit + 1 : 0;
  const lastRow = Math.min(page * limit, total);

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">NCR / CAPA</h1>
          <p className="mt-1 text-sm text-gray-500">Non-conformance reports: what was wrong, what was done with the material, why it happened and what stops it happening again.</p>
        </div>
        {canCreate && (
          <button type="button" onClick={() => setFormOpen(true)} className={primaryButtonClass}>
            <PlusIcon className="-ml-0.5 mr-2 h-4 w-4" />
            Raise Report
          </button>
        )}
      </div>

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        {stats.map((stat) => (
          <div key={stat.label} className="rounded-lg bg-white p-4 shadow-sm ring-1 ring-gray-200">
            <p className="text-xs font-medium uppercase tracking-wide text-gray-500">{stat.label}</p>
            <p className="mt-1 text-2xl font-semibold text-gray-900">{stat.value}</p>
            <p className="mt-1 text-xs text-gray-500">{stat.hint}</p>
          </div>
        ))}
      </div>

      <div className="overflow-hidden rounded-lg bg-white shadow-sm ring-1 ring-gray-200">
        <div className="flex flex-col gap-3 border-b border-gray-200 p-4 lg:flex-row lg:items-center">
          <div className="relative flex-1">
            <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <input type="search" value={searchInput} onChange={(event) => setSearchInput(event.target.value)} placeholder="Search by number, product, vendor, reference or description" aria-label="Search reports" className={cn(inputClass, 'pl-9')} />
          </div>
          <select value={statusFilter} onChange={(event) => { setStatusFilter(event.target.value as Status | ''); setPage(1); }} aria-label="Status" className={cn(inputClass, 'lg:w-40')}>
            <option value="">All statuses</option>
            {(Object.keys(STATUS) as Status[]).map((status) => (<option key={status} value={status}>{STATUS[status].label}</option>))}
          </select>
          <select value={severityFilter} onChange={(event) => { setSeverityFilter(event.target.value as Severity | ''); setPage(1); }} aria-label="Severity" className={cn(inputClass, 'lg:w-40')}>
            <option value="">All severities</option>
            {(Object.keys(SEVERITY) as Severity[]).map((severity) => (<option key={severity} value={severity}>{SEVERITY[severity].label}</option>))}
          </select>
          <label className="flex items-center gap-2 whitespace-nowrap text-sm text-gray-700">
            <input type="checkbox" checked={overdueOnly} onChange={(event) => { setOverdueOnly(event.target.checked); setPage(1); }} className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500" />
            Overdue only
          </label>
        </div>

        <div className={cn('overflow-x-auto', isFetching && data && 'opacity-60')}>
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className={thClass}>Report</th>
                <th scope="col" className={thClass}>What is wrong</th>
                <th scope="col" className={thClass}>Severity</th>
                <th scope="col" className={thClass}>Disposition</th>
                <th scope="col" className={thClass}>Target</th>
                <th scope="col" className={thClass}>Status</th>
                <th scope="col" className={cn(thClass, 'text-right')}>Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {isError ? (
                <tr><td colSpan={7} className="px-4 py-10 text-center text-sm text-gray-700">{getApiErrorMessage(error, 'The reports could not be loaded.')}</td></tr>
              ) : isLoading ? (
                <tr><td colSpan={7} className="px-4 py-10 text-center text-sm text-gray-500">Loading…</td></tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-12 text-center">
                    <p className="text-sm font-medium text-gray-900">{hasFilters ? 'No report matches' : 'No non-conformance reports yet'}</p>
                    <p className="mt-1 text-sm text-gray-500">Raise a report when something is found wrong, here or from a rejected inspection lot.</p>
                  </td>
                </tr>
              ) : (
                rows.map((row) => (
                  <tr key={row.id} className="hover:bg-gray-50">
                    <td className="whitespace-nowrap px-4 py-3">
                      <button type="button" onClick={() => setViewingId(row.id)} className="text-sm font-medium text-indigo-600 hover:text-indigo-500">{row.ncrNumber}</button>
                      <p className="mt-0.5 text-xs text-gray-500">{SOURCES[row.source]} · {formatDate(row.createdAt)}</p>
                    </td>
                    <td className="max-w-md px-4 py-3">
                      <p className="truncate text-sm text-gray-900" title={row.description}>{row.description}</p>
                      <p className="mt-0.5 truncate text-xs text-gray-500">{[row.productName, row.vendorName, row.reference].filter(Boolean).join(' · ') || '—'}</p>
                    </td>
                    <td className="whitespace-nowrap px-4 py-3"><span className={cn(badgeClass, SEVERITY[row.severity].className)}>{SEVERITY[row.severity].label}</span></td>
                    <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-700">{DISPOSITIONS[row.disposition]}</td>
                    <td className="whitespace-nowrap px-4 py-3 text-sm">
                      <span className={row.overdue ? 'font-medium text-red-600' : 'text-gray-700'}>{formatDate(row.targetDate)}</span>
                      {row.responsibleName && <span className="block text-xs text-gray-500">{row.responsibleName}</span>}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3"><span className={cn(badgeClass, STATUS[row.status].className)}>{STATUS[row.status].label}</span></td>
                    <td className="whitespace-nowrap px-4 py-3 text-right">
                      <button type="button" onClick={() => setViewingId(row.id)} className="rounded-md px-2.5 py-1 text-xs font-semibold text-indigo-600 ring-1 ring-inset ring-indigo-200 hover:bg-indigo-50" aria-label={`Open ${row.ncrNumber}`}>Open</button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="flex flex-col gap-3 border-t border-gray-200 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-gray-600">
            <span>Showing {firstRow}–{lastRow} of {formatNumber(total)}</span>
            <label className="flex items-center gap-2">
              Rows per page
              <select value={limit} onChange={(event) => { setLimit(Number(event.target.value)); setPage(1); }} className="rounded-md border border-gray-300 bg-white py-1 pl-2 pr-7 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500">
                {PAGE_SIZES.map((size) => (<option key={size} value={size}>{size}</option>))}
              </select>
            </label>
          </div>
          {totalPages > 1 && <Pagination currentPage={page} totalPages={totalPages} onPageChange={setPage} />}
        </div>
      </div>

      <Modal isOpen={formOpen} onClose={closeForm} title="Raise Non-conformance Report" description={lotFromLink ? 'For the inspection lot below.' : 'Something found wrong, in the plant or reported by a customer.'} size="xl">
        {formOpen && (
          <RaiseForm
            lotId={lotFromLink}
            isPending={create.isPending}
            onCancel={closeForm}
            onSubmit={(input) =>
              create.mutate(input, {
                onSuccess: (saved) => { toast.success(`${saved.ncrNumber} raised`); setFormOpen(false); if (lotFromLink) setSearchParams({}, { replace: true }); setViewingId(saved.id); },
                onError: (submitError) => toast.error(getApiErrorMessage(submitError, 'The report could not be raised')),
              })
            }
          />
        )}
      </Modal>

      <Modal isOpen={!!viewingId && !formOpen} onClose={() => setViewingId(null)} title={viewing?.ncrNumber ?? 'Non-conformance Report'} size="2xl">
        {viewingId && !formOpen && <NcrPanel key={viewingId} ncrId={viewingId} canChange={canChange} onClose={() => setViewingId(null)} />}
      </Modal>
    </div>
  );
};

export default NonConformances;
